import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { weaponVisual } from '../../../content/equipment/visuals.js';
import type { ProjectileState } from '../../../shared/game/combat-types.js';
import { lightningVisual, shadeEffect } from './effect-surfaces.js';
import { weaponMaterials } from '../entities/weapon-materials.js';
import { WEAPON_MODELS, type WeaponKit } from '../entities/human-weapons.js';
import { mergeRigidParts } from '../entities/rigid-batches.js';

const PROJECTILE_UP = new THREE.Vector3(0, 1, 0);
const projectileTangents = new WeakMap<THREE.Group, THREE.Vector3>();

/** Each kind's parts, built once and shared by every projectile of it, so a figure full of arrows costs two draws per arrow. */
type ShaftKind = 'arrow' | 'javelin' | 'bolt' | 'ballista-bolt';
const parts = new Map<ShaftKind, { wood: THREE.BufferGeometry; head: THREE.BufferGeometry; grain: THREE.MeshStandardMaterial; steel: THREE.MeshStandardMaterial }>();

/** Paints a part one colour, ready to merge with the others. */
function painted(geometry: THREE.BufferGeometry, color: number) {
  const flat = geometry.index ? geometry.toNonIndexed() : geometry, tint = new THREE.Color(color), count = flat.getAttribute('position').count;
  flat.setAttribute('color', new THREE.Float32BufferAttribute(Array.from({ length: count }, () => tint.toArray()).flat(), 3));
  if (!flat.hasAttribute('uv')) flat.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(count * 2), 2));
  return flat;
}

/** How each shaft is cut: length, radius, head length and width, and whether it carries the three-vane arrow fletching (a bolt's two short vanes are part of its wood). */
const SHAFTS: Record<ShaftKind, { length: number; radius: number; head: number; width: number; fletched: boolean }> = {
  arrow: { length: .72, radius: .0055, head: .055, width: .015, fletched: true }, javelin: { length: 1.15, radius: .012, head: .15, width: .026, fletched: false },
  bolt: { length: .36, radius: .0065, head: .045, width: .013, fletched: false }, 'ballista-bolt': { length: .5, radius: .0095, head: .07, width: .02, fletched: false },
};
function projectileParts(kind: ShaftKind) {
  const known = parts.get(kind);
  if (known) return known;
  const { length, radius, head: headLength, width, fletched } = SHAFTS[kind];
  // Shaft, nock and fletching are one wooden draw; the vanes are thin, so the whole draw shows both faces.
  const pieces = [painted(new THREE.CylinderGeometry(radius, radius, length, 6), 0x795438)];
  if (fletched) {
    const outline = new THREE.Shape();
    outline.moveTo(.005, -.34); outline.lineTo(.028, -.32); outline.lineTo(.023, -.245); outline.lineTo(.005, -.22); outline.closePath();
    for (let i = 0; i < 3; i++) pieces.push(painted(new THREE.ShapeGeometry(outline).rotateY(i * Math.PI * 2 / 3), 0xbdb4a0));
    pieces.push(painted(new THREE.CylinderGeometry(.0065, .0065, .022, 6).translate(0, -.365, 0), 0x795438));
  } else if (kind !== 'javelin') {
    const vane = new THREE.Shape(), tail = -length / 2;
    vane.moveTo(.004, tail); vane.lineTo(.026, tail + .01); vane.lineTo(.022, tail + .065); vane.lineTo(.004, tail + .075); vane.closePath();
    for (let i = 0; i < 2; i++) pieces.push(painted(new THREE.ShapeGeometry(vane).rotateY(i * Math.PI), 0xbdb4a0));
  }
  const wood = mergeGeometries(pieces)!; for (const piece of pieces) piece.dispose();
  const head = new THREE.ConeGeometry(width, headLength, 4).scale(1, 1, .35).translate(0, length / 2 + headLength / 2 - .008, 0);
  const finishes = weaponMaterials(), grain = (finishes.wood as THREE.MeshStandardMaterial).clone();
  grain.color.setHex(0xffffff); grain.vertexColors = true; grain.side = THREE.DoubleSide;
  const steel = finishes.blade as THREE.MeshStandardMaterial;
  // Effects dispose what they remove; these stay for the next shot.
  for (const shared of [wood, head, grain, steel]) shared.userData.shared = true;
  const made = { wood, head, grain, steel }; parts.set(kind, made); return made;
}

/** The same textured, chamfered hammer in hand and in flight; every throw shares its batched parts. */
let hammerParts: THREE.Group | undefined;
function thrownHammer() {
  if (hammerParts) return hammerParts;
  const mesh: WeaponKit['mesh'] = (parent, geometry, material, x = 0, y = 0, z = 0) => {
    const object = new THREE.Mesh(geometry, material); object.position.set(x,y,z); object.castShadow = true; parent.add(object); return object;
  };
  const kit: WeaponKit = {
    mesh, rough: false, material: weaponMaterials(),
    tube: (parent, material, top, bottom, length, x, y, z, sides = 12) => mesh(parent, new THREE.CylinderGeometry(top,bottom,length,sides),material,x,y,z),
    box: (parent, material, w,h,d,x,y,z) => mesh(parent,new THREE.BoxGeometry(w,h,d),material,x,y,z),
    ellipsoid: (parent,material,x,y,z,sx,sy,sz) => { const object = mesh(parent,new THREE.SphereGeometry(1,16,10),material,x,y,z); object.scale.set(sx,sy,sz); return object; },
  };
  const model = new THREE.Group(); model.name = 'hammer'; model.rotation.z = Math.PI; model.position.y = -.06;
  WEAPON_MODELS.throwing_hammer(kit, model); mergeRigidParts(model);
  model.traverse(object => {
    if (!(object instanceof THREE.Mesh)) return;
    object.geometry.userData.shared = true; (object.material as THREE.Material).userData.shared = true;
  });
  return hammerParts = model;
}

/** A sling stone: a worn river pebble, one shared draw. */
let stoneParts: { geometry: THREE.BufferGeometry; material: THREE.MeshStandardMaterial } | undefined;
function slingStone() {
  if (stoneParts) return stoneParts;
  const geometry = new THREE.IcosahedronGeometry(.021, 1).scale(1, 1.25, .85);
  const material = new THREE.MeshStandardMaterial({ color: 0x6f6a60, roughness: .95 });
  geometry.userData.shared = true; material.userData.shared = true;
  return stoneParts = { geometry, material };
}

/** Local +Y is the point. Dimensions match the carried weapons rather than a glowing tracer. */
export function createProjectileVisual(attackId: string): THREE.Group {
  const projectile = weaponVisual({ id: attackId, mode: 'ranged' }).projectile;
  if (projectile === 'lightning') {
    return lightningVisual();
  }
  if (projectile === 'stone') {
    const group = new THREE.Group(); group.name = 'stone-projectile';
    const { geometry, material } = slingStone(), stone = new THREE.Mesh(geometry, material); stone.name = 'stone'; stone.castShadow = true;
    group.add(stone); return group;
  }
  if (projectile === 'hammer') {
    const group = new THREE.Group(); group.name = 'hammer-projectile';
    group.add(thrownHammer().clone(true)); return group;
  }
  const kind: ShaftKind = projectile === 'javelin' || projectile === 'bolt' || projectile === 'ballista-bolt' ? projectile : 'arrow', { wood, head, grain, steel } = projectileParts(kind);
  // Bolts lodge in a target as arrows do (`CombatEffects.lodge`), and share the arrow's name for it, with their own length in `userData`.
  const group = new THREE.Group(); group.name = kind === 'javelin' ? 'javelin-projectile' : 'arrow-projectile'; group.userData.length = SHAFTS[kind].length;
  const shaft = new THREE.Mesh(wood, grain); shaft.name = 'shaft';
  const tip = new THREE.Mesh(head, steel); tip.name = 'steel-head';
  group.add(shaft, tip);
  return group;
}

/** Cosmetic, shallow trajectory. Server progress/arrival remain authoritative; no wall-clock extrapolation. */
export function poseProjectile(group: THREE.Group, shot: ProjectileState, progress: number, launchHeight = 1.44, impactHeight = 1.04, time = progress * shot.duration, ppm = 40, launchOrigin?: THREE.Vector3) {
  const p = THREE.MathUtils.clamp(progress, 0, 1);
  const fromX = launchOrigin?.x ?? shot.from.x, fromZ = launchOrigin?.z ?? shot.from.z;
  launchHeight = launchOrigin?.y ?? launchHeight;
  const dx = shot.to.x - fromX, dz = shot.to.z - fromZ, distance = Math.hypot(dx, dz);
  const ux = distance > .00001 ? dx / distance : 0, uz = distance > .00001 ? dz / distance : 1;
  // Start at the forward hand/bow, instead of the centre of the torso. Never overshoot a close target.
  const offset = launchOrigin ? 0 : Math.min(.4, distance * .2);
  const startX = fromX + ux * offset, startZ = fromZ + uz * offset;
  const projectile = weaponVisual({ id: shot.attackId, mode: 'ranged' }).projectile, javelin = projectile === 'javelin';
  // A sling stone flies fast and flat, like an arrow.
  const rise = projectile === 'lightning' ? 0 : Math.min(javelin ? .42 : .18, distance * (javelin ? .025 : .012));
  group.position.set(startX + (shot.to.x - startX) * p, launchHeight + (impactHeight - launchHeight) * p + 4 * rise * p * (1 - p), startZ + (shot.to.z - startZ) * p);
  let tangent = projectileTangents.get(group);
  if (!tangent) { tangent = new THREE.Vector3(); projectileTangents.set(group, tangent); }
  tangent.set(shot.to.x - startX, impactHeight - launchHeight + 4 * rise * (1 - 2 * p), shot.to.z - startZ);
  if (tangent.lengthSq() < .000001) tangent.set(0, 0, 1);
  group.quaternion.setFromUnitVectors(PROJECTILE_UP, tangent.normalize());
  if (projectile === 'lightning') shadeEffect(group, time, 1, p, ppm);
  // The stone tumbles in flight.
  if (projectile === 'stone') group.children[0]?.rotation.set(time * 21, time * 13, 0);
  if (projectile === 'hammer') group.children[0]?.rotation.set(time * 17, 0, 0);
}
